// src/components/ComponentDetailsModal.js
import React from 'react';

const ComponentDetailsModal = ({ component, evaluationMetadata, onClose }) => {
  // Debug logging - must be before any early returns (React Hook rules)
  React.useEffect(() => {
    if (component) {
      console.log('[ComponentDetailsModal] Component:', component.componentName);
      console.log('[ComponentDetailsModal] Component-specific metadata:', component._evaluationMetadata);
      console.log('[ComponentDetailsModal] Global metadata:', evaluationMetadata);
    }
  }, [component, evaluationMetadata]);

  if (!component) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get context data (array of evidence strings)
  const contextArray = component.context || [];
  const hasContext = contextArray && contextArray.length > 0;
  const detectionCount = hasContext ? contextArray.length : 0;
  
  // Prefer component-specific metadata over global metadata
  const metadata = component._evaluationMetadata || evaluationMetadata || {};
  const communications = metadata.communications_in_window || [];
  const communicationCount = metadata.communication_count || 0;
  
  // Format communication date
  const formatCommDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  // Get icon for record type
  const getRecordTypeIcon = (recordType) => {
    switch(recordType) {
      case 'email':
        return '✉️';
      case 'call':
        return '📞';
      case 'slack':
        return '💬';
      default:
        return '📄';
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900">
                {component.componentName}
              </h3>
              {/* Badge color depends on whether this meets expectations */}
              {(() => {
                const isNegative = component.expectedValue === false;
                // Use new meetsExpectation logic if available
                const meetsExpectation = typeof component.meetsExpectation !== 'undefined'
                    ? component.meetsExpectation
                    : component.isDetected === component.expectedValue;

                return (
              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                    meetsExpectation
                  ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
              }`}>
                    {/* New Status Text based on Polarity or Fallback */}
                    {typeof component.meetsExpectation !== 'undefined'
                        ? (meetsExpectation ? 'Positive Signal' : 'Risk Detected')
                        : (isNegative 
                      ? (component.isDetected ? 'Risk Detected' : 'Not Detected (Good)')
                            : (component.isDetected ? 'Detected' : 'Not Detected'))}
              </span>
                );
              })()}
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {component.groupName} • {component.groupWeight}% weight
              {component.expectedValue === false && (
                <span className="ml-2 text-amber-600 font-medium">(Negative Metric)</span>
              )}
            </p>
            {hasContext && (
              <p className={`text-sm mt-1 font-medium ${
                // Use meetsExpectation logic for color
                (typeof component.meetsExpectation !== 'undefined' ? !component.meetsExpectation : (component.expectedValue === false && component.isDetected))
                  ? 'text-red-600'
                  : 'text-blue-600'
              }`}>
                {detectionCount} instance{detectionCount !== 1 ? 's' : ''} detected
                {/* Show polarity summary if available */}
                {(component.positiveCount !== undefined || component.negativeCount !== undefined) && (
                    <span className="ml-2 text-gray-600">
                        ({component.positiveCount || 0} Positive / {component.negativeCount || 0} Negative)
                    </span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-4"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {hasContext ? (
            <div className="space-y-6">
                      {/* Communication Summary */}
                      {communicationCount > 0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">
                            Related Communication{communicationCount !== 1 ? 's' : ''} ({communicationCount})
                          </h4>
                          <p className="text-xs text-gray-600 mb-3">
                            Communications where this component was detected
                          </p>
                          <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                            {communications.slice(0, 10).map((comm, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs text-gray-600 bg-white p-2 rounded border border-gray-100">
                        <span className="text-base">{getRecordTypeIcon(comm.record_type)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-700 truncate">{comm.title}</div>
                          <div className="text-gray-500">{formatCommDate(comm.date)}</div>
                        </div>
                      </div>
                    ))}
                    {communicationCount > 10 && (
                      <div className="text-xs text-gray-500 italic text-center mt-1">
                        + {communicationCount - 10} more communications
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Evidence Quotes - reversed to show most recent first */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Evidence Quotes</h4>
                <div className="space-y-3">
                  {[...contextArray].reverse().map((contextItem, idx) => {
                    // Normalize context item: handle both string (legacy) and object (new) formats
                    let quoteText = '';
                    let polarity = null;
                    
                    if (typeof contextItem === 'string') {
                        quoteText = contextItem;
                    } else if (contextItem && typeof contextItem === 'object') {
                        quoteText = contextItem.quote || '';
                        polarity = contextItem.polarity;
                    }

                    const boxStyle = polarity === 'positive' 
                        ? { backgroundColor: 'var(--pallium-success-green-light, #ecfdf5)', borderColor: 'var(--pallium-success-green, #10b981)' }
                        : polarity === 'negative'
                        ? { backgroundColor: 'var(--pallium-error-red-light, #fef2f2)', borderColor: 'var(--pallium-error-red, #ef4444)' }
                        : { backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }; // Default blue

                    const indexStyle = polarity === 'positive'
                        ? { backgroundColor: 'var(--pallium-success-green, #10b981)' }
                        : polarity === 'negative'
                        ? { backgroundColor: 'var(--pallium-error-red, #ef4444)' }
                        : { backgroundColor: '#2563eb' }; // Default blue

                    return (
                    <div 
                      key={idx} 
                      className="rounded-md p-4 border"
                      style={boxStyle}
                    >
                      <div className="flex items-start gap-3">
                        <span 
                            className="flex-shrink-0 w-6 h-6 text-white rounded-full flex items-center justify-center text-xs font-semibold"
                            style={indexStyle}
                        >
                          {idx + 1}
                        </span>
                        <div className="flex-1">
                            <p className="text-sm" style={{ color: 'var(--pallium-text-primary, #111827)' }}>
                              {quoteText}
                              {polarity === 'positive' && (
                                <span className="ml-2 font-bold text-xs" style={{ color: 'var(--pallium-success-green-dark, #047857)' }}>(+1)</span>
                              )}
                              {polarity === 'negative' && (
                                <span className="ml-2 font-bold text-xs" style={{ color: 'var(--pallium-error-red-dark, #b91c1c)' }}>(-1)</span>
                              )}
                            </p>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="py-12 text-center">
              {(() => {
                const isNegative = component.expectedValue === false;
                const isGoodOutcome = !component.isDetected && isNegative; // Not detected negative metric = good
                
                return (
                  <>
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                      isGoodOutcome ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      {isGoodOutcome ? (
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                      )}
              </div>
                    <h4 className={`text-base font-medium mb-2 ${
                      isGoodOutcome ? 'text-green-900' : 'text-gray-900'
                    }`}>
                      {component.isDetected ? 'No Context Available' : 
                        isGoodOutcome ? 'No Risk Detected' : 'Not Detected'}
              </h4>
              <p className="text-sm text-gray-600 max-w-md mx-auto">
                {component.isDetected 
                  ? 'This component was detected but no detailed context is available for the current evaluation period.'
                        : isNegative
                        ? 'No instances of this risk indicator were detected in communications during the current evaluation period. This is a positive health signal.'
                  : 'This component was not detected in communications during the current evaluation period.'}
              </p>
                  </>
                );
              })()}
            </div>
          )}

          {/* Polarity Breakdown (New) or Expected Value (Legacy) */}
          {/* Prioritize Polarity Breakdown if netPolarity is available */}
          {/* Removed Summary Section as per user request */}
          {typeof component.netPolarity === 'undefined' && component.expectedValue !== undefined && (
             /* Legacy Expected Value Section - Only show for legacy data */
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Expected Value:</span>
                <span className={`text-sm font-semibold ${
                  component.isDetected === component.expectedValue 
                    ? 'text-green-600' 
                    : 'text-amber-600'
                }`}>
                  {component.expectedValue ? 'Positive' : 'Negative'}
                </span>
                {component.isDetected === component.expectedValue && (
                  <span className="text-xs text-green-600">
                    (Meets expectation)
                  </span>
                )}
                {component.isDetected !== component.expectedValue && (
                  <span className="text-xs text-amber-600">
                    (Does not meet expectation)
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComponentDetailsModal;

